import { useEffect, useState } from 'react';

const POLL_MS = 250;
// RMS of the waveform (0–1) above which someone counts as talking.
const SPEAKING_LEVEL = 0.035;

// Who is talking right now, for the call's "Speaking" ring. Reads each
// stream's audio locally through an AnalyserNode; nothing is sent anywhere.
export function useSpeaking(
  streams: Record<string, MediaStream | null>
): Set<string> {
  const [speaking, setSpeaking] = useState<Set<string>>(() => new Set());
  // Re-wire only when the set of audio tracks changes, not on every render.
  const key = Object.entries(streams)
    .map(
      ([id, stream]) =>
        `${id}:${
          stream
            ?.getAudioTracks()
            .map((t) => t.id)
            .join(',') ?? ''
        }`
    )
    .sort()
    .join('|');

  useEffect(() => {
    const AudioContextClass =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextClass) return;

    const context = new AudioContextClass();
    void context.resume().catch(() => undefined);
    const analysers: Array<[string, AnalyserNode]> = [];
    for (const [id, stream] of Object.entries(streams)) {
      if (!stream || stream.getAudioTracks().length === 0) continue;
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      // Analysis only: the node is not connected to the speakers.
      context.createMediaStreamSource(stream).connect(analyser);
      analysers.push([id, analyser]);
    }

    const buffer = new Uint8Array(512);
    const timer = setInterval(() => {
      const next = new Set<string>();
      for (const [id, analyser] of analysers) {
        analyser.getByteTimeDomainData(buffer);
        let sum = 0;
        for (const sample of buffer) {
          const value = (sample - 128) / 128;
          sum += value * value;
        }
        if (Math.sqrt(sum / buffer.length) > SPEAKING_LEVEL) next.add(id);
      }
      setSpeaking((current) =>
        current.size === next.size && [...next].every((id) => current.has(id))
          ? current
          : next
      );
    }, POLL_MS);

    return () => {
      clearInterval(timer);
      void context.close().catch(() => undefined);
      setSpeaking(new Set());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the audio tracks
  }, [key]);

  return speaking;
}
